import React, { useEffect, useState } from "react";

import { LoadingButton } from "@mui/lab";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";

import {
  MarketplaceExtractAttributeGroup,
  MarketplaceGenerateAttribute,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_marketplace_pb";

import {
  getMarketplaceExtractAttributeGroups,
  getMarketplaceGenerateAttributes,
} from "api/endpoints/marketplace";
import { callOptionsHeaders } from "api/helpers";
import GenericDialog from "components/shared/generic-dialog";
import { DEFAULT_REGION_CODE } from "constants/index";

export interface NewJobFormState {
  name: string;
  urlListStr: string;
  extractAttributesGroupId: string;
  generateAttributeIds: string[];
}

interface AddJobFormProps {
  isOpen: boolean;
  handleClose: () => void;
  newJobFormState: NewJobFormState;
  setNewJobFormState: (newState: NewJobFormState) => void;
  handleSubmit: () => void;
  isLoading: boolean;
  isDisabled: boolean;
}

const AddJobForm = ({
  isOpen,
  handleClose,
  newJobFormState,
  setNewJobFormState,
  handleSubmit,
  isLoading,
  isDisabled,
}: AddJobFormProps) => {
  const [extractAttributeGroups, setExtractAttributeGroups] = useState<
    MarketplaceExtractAttributeGroup[]
  >([]);
  const [generateAttributes, setGenerateAttributes] = useState<
    MarketplaceGenerateAttribute[]
  >([]);
  const [selectedGenerateAttributes, setSelectedGenerateAttributes] = useState<
    string[]
  >([]);

  useEffect(() => {
    const init = async () => {
      const extractAttributeGroups = await getMarketplaceExtractAttributeGroups(
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );
      setExtractAttributeGroups(extractAttributeGroups.attributeGroups);

      const generateAttributes = await getMarketplaceGenerateAttributes(
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );
      setGenerateAttributes(generateAttributes.attributes);
    };
    init();
  }, []);

  return (
    <GenericDialog
      title="Add Job"
      width="md"
      hasXCloseBtn
      open={isOpen}
      handleDialogClose={handleClose}
      data-testid="add-job-dialog"
    >
      <Grid xs={12} item>
        <form noValidate autoComplete="off">
          <Box
            sx={{ height: "70vh", width: "100%" }}
            display="flex"
            flexDirection="column"
            gap={4}
            marginTop={4}
          >
            <TextField
              required
              label="Name"
              inputProps={{
                "data-testid": "add-job-name-field",
              }}
              value={newJobFormState.name}
              onChange={(event) =>
                setNewJobFormState({
                  ...newJobFormState,
                  name: event.target.value,
                })
              }
            />
            <TextField
              required
              label="Url List (comma separated)"
              inputProps={{
                "data-testid": "add-job-url-field",
              }}
              value={newJobFormState.urlListStr}
              onChange={(event) =>
                setNewJobFormState({
                  ...newJobFormState,
                  urlListStr: event.target.value,
                })
              }
            />
            <FormControl sx={{ minWidth: 100 }} size="small">
              <InputLabel id="add-job-extract-group-label">
                Extract Group
              </InputLabel>
              <Select
                labelId="add-job-extract-group-label"
                id="add-job-extract-group"
                input={
                  <OutlinedInput
                    id="select-multiple-chip"
                    label="Extract Group"
                  />
                }
                onChange={(event) => {
                  setNewJobFormState({
                    ...newJobFormState,
                    extractAttributesGroupId: event.target.value as string,
                  });
                }}
                value={newJobFormState.extractAttributesGroupId}
                size="small"
                inputProps={{
                  "data-testid": "add-job-extract-group-field",
                }}
              >
                {extractAttributeGroups.map((group) => (
                  <MenuItem value={group.id} key={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="add-job-generate-attributes-label">
                Generate Attributes
              </InputLabel>
              <Select
                labelId="add-job-generate-attributes-label"
                id="add-job-generate-attributes-field"
                multiple
                value={selectedGenerateAttributes}
                onChange={(event) => {
                  const { value } = event.target;
                  const selectedOptionStrings =
                    typeof value === "string" ? value.split(",") : value;

                  const selectedOptions = selectedOptionStrings.map(
                    (optionString) => {
                      const attribute = generateAttributes.find(
                        (row) => row.id === optionString,
                      );
                      return attribute ? attribute : null;
                    },
                  );

                  const filteredSelectedOptions = selectedOptions.filter(
                    (option) => option !== null,
                  ) as MarketplaceGenerateAttribute[];

                  // Extract the IDs from the selected options
                  const selectedIds = filteredSelectedOptions.map(
                    (option) => option.id,
                  );

                  setSelectedGenerateAttributes(
                    filteredSelectedOptions.map((option) => option.id),
                  );
                  setNewJobFormState({
                    ...newJobFormState,
                    generateAttributeIds: selectedIds,
                  });
                }}
                input={
                  <OutlinedInput
                    id="add-job-generate-attributes-chip"
                    label="Generate Attributes"
                  />
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => {
                      const attribute = generateAttributes.find(
                        (row) => row.id === value,
                      );
                      if (!attribute) return null;
                      return <Chip key={attribute.id} label={attribute.name} />;
                    })}
                  </Box>
                )}
              >
                {generateAttributes.map((attribute) => (
                  <MenuItem key={attribute.id} value={attribute.id}>
                    {attribute.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <LoadingButton
            color="primary"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isDisabled}
            loadingPosition="center"
            variant="contained"
            data-testid="add-job-btn"
          >
            Add Job
          </LoadingButton>
        </form>
      </Grid>
    </GenericDialog>
  );
};

export default AddJobForm;
